/**
 * User for Dashboard
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { pick } from 'lodash-es';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Alert, Card } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useApi2, useApi2call } from 'containers/Api2/index.js';
import { EMPLOYEE_ROLE, STUDENT_ROLE } from 'containers/User/constants.js';
import Breadcrumbs from 'components/layout/Breadcrumbs/index.jsx';
import Spinner from 'components/Spinner/index.jsx';
import DeleteButton from 'components/interaction/DeleteButton/index.jsx';
import UserHeader from 'components/user/UserModal/UserHeader.jsx';
import useUpdateAvatar from 'components/user/UploadAvatar/useUpdateAvatar.js';
import UploadModal from 'components/user/UploadAvatar/UploadModal.jsx';
import { idValues } from 'utils/arrays.js';
import getUserFields from './getUserFields.jsx';
import InfoGroup from './InfoGroup.jsx';
import InfoList from './InfoList.jsx';
import Connection from './Connection.jsx';
import messages from '../messages.js';
import Group from './Group.jsx';
import AdminGroup from './AdminGroup.jsx';
import InviteParentControl from './InviteParentControl.jsx';
import AddGroupsControl from './AddGroupsControl.jsx';
import AddAdminGroupsControl from './AddAdminGroupsControl.jsx';
import adminParser from '../../adminParser.js';
import prepareBody from '../prepareBody.js';
import InviteEmployeeControl from './InviteEmployeeControl.jsx';
import adminImage from './admin.png';
import makeUrlSelector from '../makeUrlSelector.js';
import useIsFoundation from '../../useIsFoundation.js';
import AccountCard from './AccountCard.jsx';

const AdminCard = styled(Card)`
  width: 100%;
  img {
    width: 48px;
  }
  @media (min-width: 576px) {
    width: 50%;
  }
`;

function User(props) {
  const { setMeta } = props;
  const { roleName, userId } = useParams();
  const { url, selector } = makeUrlSelector(roleName, userId);
  const loadOptions = { url, selector, version: 2, parser: adminParser, refresh: true };
  const user = useApi2(loadOptions);
  const [show, setShow] = useState(false);
  const api2call = useApi2call();
  const navigate = useNavigate();
  const isFoundation = useIsFoundation();
  const parentLink = `/admin/users#${roleName}`;
  const parts = [
    { href: parentLink, label: messages.users },
    { label: user.accountName },
  ];
  const confirmRemoveText = { ...messages.confirmRemoveUser, values: user };

  const reload = () => api2call({ ...loadOptions, reload: true, ignore: [404] });

  const showParent = (conn) => <Connection conn={conn} user={user} linkPage="parent" canDelete={!isFoundation} />;

  function showChild(conn) {
    // When the last connection is deleted, redirect to parent list
    const onDelete = user.connections.list.length === 1 ? () => navigate(parentLink, { replace: true }) : undefined;
    return <Connection conn={conn} user={user} linkPage="student" onChange={reload} onDelete={onDelete} canDelete={!isFoundation} />;
  }

  const showGroup = (group) => <Group group={group} user={user} />;

  const showAdminGroup = (group) => <AdminGroup group={group} user={user} onChange={reload} />;

  const infoGroups = getUserFields(user, roleName, isFoundation);
  const editFields = infoGroups?.map((g) => g.fields.map((f) => f.editSchema?.properties)).flat(2).filter(Boolean).map((f) => f.id);

  const handleDelete = async () => {
    await api2call({
      method: 'DELETE',
      url,
      selector,
      version: 2,
      success: messages.userDeleted,
    });
    navigate(parts[0].href);
  };

  const marginClasses = 'ms-sm-3 mt-3 mt-sm-0';
  const showHeaderControls = roleName === 'staff' ? isFoundation : !isFoundation;
  const header = (
    <div className="d-flex align-items-baseline justify-content-between flex-wrap flex-sm-nowrap">
      <Breadcrumbs parts={parts} className="flex-grow-1 card-title input-height" />
      {showHeaderControls && (
        <span className="d-flex flex-column align-items-end flex-sm-row align-items-sm-baseline flex-grow-1 flex-sm-grow-0">
          <InviteEmployeeControl user={user} className={marginClasses} />
          <DeleteButton
            variant="light text-danger"
            icon="ss-trash"
            onClick={handleDelete}
            confirmText={confirmRemoveText}
            className={marginClasses}
          >
            <FormattedMessage {...messages.removeUser} />
          </DeleteButton>
        </span>
      )}
    </div>
  );

  useEffect(() => {
    setMeta({
      title: [user.accountName, messages[roleName]],
      header,
    });
  }, [user.accountName]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(info) {
    const body = prepareBody(pick(info, editFields));
    api2call({ method: 'PUT', url, selector, body, version: 2, parser: adminParser });
  }

  const connectionsList = idValues(user.connections);
  const updateAvatar = useUpdateAvatar({
    url: `${url}/avatar`,
    selector,
    version: 2,
  });

  if (!['student', 'parent', 'employee', 'staff'].includes(roleName)) {
    return <Alert variant="danger">Unknown role</Alert>;
  }

  if (user.loading) {
    return <Spinner />;
  }

  const canUpdateAvatar = !isFoundation && [EMPLOYEE_ROLE, STUDENT_ROLE].includes(user.roleType);
  return (
    <Card className="border-0 mt-4">
      <Card.Header className="bg-primary border-0">
        <UserHeader user={user} onEdit={canUpdateAvatar ? () => setShow(true) : undefined} />
        {canUpdateAvatar && <UploadModal show={show} user={user} onChange={updateAvatar} onHide={() => setShow(false)} />}
      </Card.Header>
      <Card.Body>
        {infoGroups?.map((g) => (
          <InfoGroup
            key={g.title.id}
            {...g}
            user={user}
            onChange={handleChange}
            className="mb-5"
          />
        ))}
        {roleName === 'parent' && (
          <InfoList
            title={messages.children}
            items={connectionsList}
            showItem={showChild}
            className="mb-5"
          />
        )}
        {roleName === 'student' && (
          <InfoList
            title={messages.parents}
            items={connectionsList}
            showItem={showParent}
            control={!isFoundation && (
              <InviteParentControl user={user} variant="link" className="p-0">
                <FormattedMessage {...messages.invite} />
              </InviteParentControl>
            )}
            className="mb-5"

          />
        )}
        <AccountCard user={user} selector={url} />
        <InfoList
          title={messages.groups}
          items={idValues(user.groups)}
          showItem={showGroup}
          control={<AddGroupsControl user={user} className="p-0" />}
          className="mb-5"
        />
        {!isFoundation && roleName === 'student' && (
          <InfoList
            title={messages.adminGroups}
            items={idValues(user.administrativeGroups)}
            showItem={showAdminGroup}
            control={<AddAdminGroupsControl user={user} onChange={reload} className="p-0" />}
            className="mb-5"
          />
        )}
        {!isFoundation && roleName === 'student' && (
          <>
            <h3><FormattedMessage {...messages.more} /></h3>
            <AdminCard className="border rounded-md">
              <Card.Body className="d-flex align-items-start">
                <img src={adminImage} alt="" />
                <div className="ms-4">
                  <h3 className="text-gray"><FormattedMessage {...messages.administration} /></h3>
                  <p><FormattedMessage {...messages.administrationInfo} /></p>
                  <div>
                    <Link to={`/admin/users/admin/${user.id}`} className="btn btn-outline-secondary d-inline-block"><FormattedMessage {...messages.administrationLink} /></Link>
                  </div>
                </div>
              </Card.Body>
            </AdminCard>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

User.propTypes = {
  match: PropTypes.object,
  setMeta: PropTypes.func,
};

export default User;
